import {
  buildProductConditionKey,
  parseProductConditionKey,
} from "@/util/conditional-logic-helpers";
import { CONDITIONAL_OPERATIONS } from "@/util/constants";
import type { ProductCatalog, ProductCatalogVariant } from "@/util/product-conditional-logic";

/**
 * Four IF operators, two persisted. "Pricing option is/is not" differs only by the key
 * shape written to `selectedField`, and the runtime already flags both keys for a
 * selected variant (`buildProductSelectionData`), so it needs no runtime change.
 *
 *   Is selected / Is not selected    -> product::<id>::*          + isSelected/isNotSelected
 *   Pricing option is / is not       -> product::<id>::<priceId>  + isSelected/isNotSelected
 *
 * `inputValue` stays empty: these are value-less operators on disk.
 */
export const PRICING_OPTION_IS = "pricingOptionIs";
export const PRICING_OPTION_IS_NOT = "pricingOptionIsNot";

export type ProductIfOperator =
  | typeof CONDITIONAL_OPERATIONS.IS_SELECTED
  | typeof CONDITIONAL_OPERATIONS.IS_NOT_SELECTED
  | typeof PRICING_OPTION_IS
  | typeof PRICING_OPTION_IS_NOT;

/** The two operators that carry a pricing option. */
export const isPricingOptionOperator = (operator?: string | null): boolean =>
  operator === PRICING_OPTION_IS || operator === PRICING_OPTION_IS_NOT;

/** Products collapse to their any-variant key so the dropdown holds steady while the
 * operator switches the stored key between `::*` and a variant. */
export const toProductIdentityKey = (key?: string | null): string | null => {
  const ref = parseProductConditionKey(key);
  if (!ref) {
    return null;
  }
  if (ref.kind === "bumpProduct") {
    return ref.priceId ? key ?? null : null;
  }
  return buildProductConditionKey({
    kind: "product",
    productId: ref.productId,
    priceId: null,
  });
};

/** Which of the four UI operators a persisted (field, operation) pair represents. */
export const resolveProductIfOperator = (
  selectedField?: string | null,
  selectedOperation?: string | null
): ProductIfOperator | null => {
  const ref = parseProductConditionKey(selectedField);
  if (!ref) {
    return null;
  }
  const negated = selectedOperation === CONDITIONAL_OPERATIONS.IS_NOT_SELECTED;
  // Only a main product can name a pricing option; a bump has exactly one price.
  if (ref.kind === "product" && ref.priceId !== null) {
    return negated ? PRICING_OPTION_IS_NOT : PRICING_OPTION_IS;
  }
  return negated
    ? CONDITIONAL_OPERATIONS.IS_NOT_SELECTED
    : CONDITIONAL_OPERATIONS.IS_SELECTED;
};

/** The pricing option a condition targets, or `null` when it targets the whole product. */
export const resolvePricingOptionId = (selectedField?: string | null): string | null => {
  const ref = parseProductConditionKey(selectedField);
  return ref && ref.kind === "product" ? ref.priceId : null;
};

export interface ProductConditionFields {
  selectedField: string;
  selectedOperation: string;
}

/**
 * Back to the persisted pair. `priceId` is used only by the pricing operators — the
 * current option, or the product's first when switching into pricing mode.
 */
export const applyProductIfOperator = (
  selectedField: string,
  operator: ProductIfOperator,
  priceId?: string | null
): ProductConditionFields | null => {
  const ref = parseProductConditionKey(selectedField);
  if (!ref) {
    return null;
  }

  const negated =
    operator === CONDITIONAL_OPERATIONS.IS_NOT_SELECTED || operator === PRICING_OPTION_IS_NOT;
  const selectedOperation = negated
    ? CONDITIONAL_OPERATIONS.IS_NOT_SELECTED
    : CONDITIONAL_OPERATIONS.IS_SELECTED;

  // A bump always refers to its single price, whichever operator is chosen.
  if (ref.kind === "bumpProduct") {
    return { selectedField, selectedOperation };
  }

  // Falling back to the any-variant key when no option is known keeps the pair readable:
  // a pricing key with a missing price would read back as a whole-product condition.
  const nextPriceId = isPricingOptionOperator(operator) ? priceId ?? null : null;

  return {
    selectedField: buildProductConditionKey({
      kind: "product",
      productId: ref.productId,
      priceId: nextPriceId,
    }),
    selectedOperation,
  };
};

/** The pricing options offered for a condition, empty for bumps and unknown products. */
export const pricingOptionsForField = (
  selectedField: string | null | undefined,
  catalog: ProductCatalog
): ProductCatalogVariant[] => {
  const ref = parseProductConditionKey(selectedField);
  if (!ref || ref.kind === "bumpProduct") {
    return [];
  }
  return catalog.products.find((product) => product.productId === ref.productId)?.variants ?? [];
};

/** Hidden for bumps and single-option products: "Is selected" already says the same. */
export const supportsPricingOptions = (
  selectedField: string | null | undefined,
  catalog: ProductCatalog
): boolean => pricingOptionsForField(selectedField, catalog).length > 1;

/** UI sentinel only, never persisted: `selectedField` always holds a real product key. */
export const PRODUCT_FIELD_SENTINEL = "sellProduct";

/** True when a stored field key is a product or bump target. */
export const isProductConditionField = (key?: string | null): boolean =>
  parseProductConditionKey(key) !== null;

/** The value the IF field dropdown binds to: every product target shows one entry. */
export const toConditionFieldValue = (key?: string | null): string | null => {
  if (!key) {
    return null;
  }
  return isProductConditionField(key) ? PRODUCT_FIELD_SENTINEL : key;
};

/** The value the product dropdown binds to: the product's identity key. */
export const toProductSelectValue = (key?: string | null): string | null =>
  toProductIdentityKey(key);

/**
 * Carries the operator's intent to a newly chosen product. A bump or single-option
 * product falls back to the whole-product form, where pricing options do not apply.
 */
export const applyProductSelection = (
  productIdentityKey: string,
  previousOperator: ProductIfOperator | null,
  catalog: ProductCatalog
): ProductConditionFields | null => {
  const operator = previousOperator ?? CONDITIONAL_OPERATIONS.IS_SELECTED;
  const keepsPricing =
    isPricingOptionOperator(operator) && supportsPricingOptions(productIdentityKey, catalog);
  const nextOperator = keepsPricing
    ? operator
    : operator === PRICING_OPTION_IS_NOT || operator === CONDITIONAL_OPERATIONS.IS_NOT_SELECTED
    ? CONDITIONAL_OPERATIONS.IS_NOT_SELECTED
    : CONDITIONAL_OPERATIONS.IS_SELECTED;

  return applyProductIfOperator(
    productIdentityKey,
    nextOperator as ProductIfOperator,
    keepsPricing ? pricingOptionsForField(productIdentityKey, catalog)[0]?.priceId ?? null : null
  );
};

/** The first selectable product target, used when "Sell Product" is first chosen. */
export const firstProductTargetKey = (catalog: ProductCatalog): string | null => {
  const product = catalog.products[0];
  if (product) {
    return buildProductConditionKey({
      kind: "product",
      productId: product.productId,
      priceId: null,
    });
  }
  const bump = catalog.bumps[0];
  return bump
    ? buildProductConditionKey({
        kind: "bumpProduct",
        productId: bump.productId,
        priceId: bump.priceId,
      })
    : null;
};
